import { gql, type ApolloClient } from "@apollo/client";

/**
 * Presigned-PUT upload for the file importer.
 *
 * `react-sheet-uploader` runs entirely in the browser, so the reviewed rows
 * have to be parked somewhere the backend can read: we PUT them into our own
 * bucket and pass the key to `handleImportUploadId`.
 *
 * Same presigned flow as `~/hooks/_file-upload`, without the upload toast —
 * the importer shows its own blocking overlay while this runs.
 */
const SAFE_SIGN_S3_MUTATION = gql`
  mutation SignImportUpload(
    $filename: String!
    $filetype: String!
    $type: String
    $forceCustomPath: Boolean
    $forceUniqueFilename: Boolean
  ) {
    _safeSignS3(
      filename: $filename
      filetype: $filetype
      type: $type
      forceCustomPath: $forceCustomPath
      forceUniqueFilename: $forceUniqueFilename
    ) {
      url
      signedRequest
    }
  }
`;

/** Everything the importer writes lands under this prefix in the bucket. */
const IMPORT_UPLOAD_FOLDER = "imports";

/** Strips the CDN origin so the backend receives a plain object key. */
const toObjectKey = (url: string): string => {
  try {
    return decodeURIComponent(new URL(url).pathname.replace(/^\//, ""));
  } catch {
    // Already a key rather than a url.
    return url;
  }
};

interface SignedUpload {
  signedRequest: string;
  url: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Client = ApolloClient<any>;

const sign = async (
  client: Client,
  filename: string,
  filetype: string,
): Promise<SignedUpload> => {
  const { data } = await client.mutate({
    mutation: SAFE_SIGN_S3_MUTATION,
    variables: {
      filename,
      filetype,
      type: IMPORT_UPLOAD_FOLDER,
      // Keeps everything under `imports/` rather than the per-user media path.
      forceCustomPath: true,
      forceUniqueFilename: true,
    },
  });

  const { signedRequest, url } = data?._safeSignS3 ?? {};
  if (!signedRequest || !url) {
    throw new Error("Could not sign the import upload");
  }
  return { signedRequest, url };
};

const put = async (
  signedRequest: string,
  body: Blob | File,
  contentType: string,
): Promise<void> => {
  const response = await fetch(signedRequest, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body,
  });
  if (!response.ok) {
    throw new Error(`Import upload failed (${response.status})`);
  }
};

export interface UploadedImport {
  /** S3 key of the reviewed rows, handed to `handleImportUploadId`. */
  dataKey: string;
  /** S3 key of the file the user originally chose, kept for auditing. */
  originalFileKey: string | null;
}

/**
 * Uploads the reviewed rows, and the original file when there was one.
 *
 * The rows are what the backend imports. The original file is archived purely
 * so an import can be traced back to exactly what the user submitted — a
 * failure to archive it must not sink an otherwise good import, so it is
 * uploaded on a best-effort basis.
 */
export const uploadImportToS3 = async (
  client: Client,
  {
    rows,
    originalFile,
    templateId,
  }: {
    rows: unknown[];
    originalFile?: File | null;
    templateId: string;
  },
): Promise<UploadedImport> => {
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  const safeTemplateId = String(templateId || "import").replace(
    /[^a-zA-Z0-9-_]/g,
    "",
  );

  const dataUpload = await sign(
    client,
    `${safeTemplateId}-${stamp}.json`,
    "application/json",
  );
  await put(
    dataUpload.signedRequest,
    new Blob([JSON.stringify(rows)], { type: "application/json" }),
    "application/json",
  );

  let originalFileKey: string | null = null;
  if (originalFile) {
    try {
      const contentType = originalFile.type || "application/octet-stream";
      const fileUpload = await sign(
        client,
        `${safeTemplateId}-${stamp}-source-${originalFile.name}`,
        contentType,
      );
      await put(fileUpload.signedRequest, originalFile, contentType);
      originalFileKey = toObjectKey(fileUpload.url);
    } catch (error) {
      // Archiving is best-effort; the import itself is already safely stored.
      // eslint-disable-next-line no-console
      console.warn("Could not archive the original import file", error);
    }
  }

  return { dataKey: toObjectKey(dataUpload.url), originalFileKey };
};
